import { useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import queryString from "query-string";
import includes from "lodash/fp/includes";

import { setBookingSource, updateCommonSearchParams } from "features/booking/bookingSlice";
import { selectCurrentSourceId, getSourceById } from "utils/sourceUtils";
import Routes from "config/routes";
import { BOOKING_SOURCES } from "utils/constants";

/**
 * Custom hook to manage application booking source
 * Consolidates source logic and provides the current source object
 */

export const useSourceManager = (history) => {
  const dispatch = useDispatch();

  // Redux state
  const currentSourceId = useSelector(selectCurrentSourceId);
  const hasReservationInProgress =
    useSelector((state) => state.booking.roomReservations).length > 0;
  const hasBookingConfirmation = useSelector((state) =>
    Boolean(state.booking.bookingConfirmationId),
  );

  // Route checks
  const isOnPlanStay = includes(Routes.PLAN_STAY)(history.location.pathname);
  const isOnChooseRoom = includes(Routes.CHOOSE_ROOM)(history.location.pathname);

  // Memoized source object
  const currentSource = useMemo(() => {
    return getSourceById(currentSourceId);
  }, [currentSourceId]);

  // Update source when dependencies change
  useEffect(() => {
    // Source determination logic
    const determineSource = () => {
      const searchParams = queryString.parse(history.location.search);

      // Handle redirects with source parameter
      if (searchParams.source && typeof searchParams.source === "string") {
        const sourceValue = searchParams.source.toLowerCase();

        // Find matching source by ID
        const matchingSource = Object.values(BOOKING_SOURCES).find(
          (source) => source.id?.toLowerCase() === sourceValue,
        );

        if (matchingSource) {
          return matchingSource.id;
        }
      }

      // Handle Redirect from soho
      if (isOnChooseRoom && searchParams.source === BOOKING_SOURCES.SOHO.id) {
        return BOOKING_SOURCES.SOHO.id;
      }

      // Handle Redirect from Mollies.com
      if (isOnChooseRoom && !searchParams.source && !hasReservationInProgress) {
        return BOOKING_SOURCES.DEFAULT.id;
      }

      // Handle Refresh on other pages of Booking Flow including Multi-Room Booking Flow
      if (
        currentSourceId === BOOKING_SOURCES.SOHO.id &&
        !isOnPlanStay &&
        (hasReservationInProgress || hasBookingConfirmation)
      ) {
        return BOOKING_SOURCES.SOHO.id;
      }

      // Handle other specific sources
      if (
        currentSourceId &&
        currentSourceId !== BOOKING_SOURCES.DEFAULT.id &&
        !isOnPlanStay &&
        (hasReservationInProgress || hasBookingConfirmation)
      ) {
        return currentSourceId;
      }

      return BOOKING_SOURCES.DEFAULT.id;
    };

    const requiredSource = determineSource();
    if (currentSourceId !== requiredSource) {
      dispatch(setBookingSource(requiredSource));
      // Use specific action to avoid race conditions with other hooks
      dispatch(updateCommonSearchParams({ source: requiredSource }));
    }
  }, [
    history.location.pathname,
    history.location.search,
    hasReservationInProgress,
    hasBookingConfirmation,
    currentSourceId,
    dispatch,
  ]);

  return {
    currentSource,
    currentSourceId,
  };
};

export default useSourceManager;
